import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebase'; 
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot } from 'firebase/firestore';
import './ManageCategoriesPage.css'; // Make sure the CSS is imported

// --- Reusable Modal for Editing a Category ---
function EditCategoryModal({ category, isOpen, onClose, onSave, isSaving }) {
  const [name, setName] = useState('');
  const [flagMessage, setFlagMessage] = useState('');
  const [iconUrl, setIconUrl] = useState('');

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setFlagMessage(category.flagMessage || '');
      setIconUrl(category.iconUrl || ''); // Load existing URL
    }
  }, [category]);

  if (!isOpen || !category) return null;

  const handleSave = (e) => {
    e.preventDefault();
    onSave({
      id: category.id,
      name,
      flagMessage,
      iconUrl,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-category-modal">
        <h2>Edit Category: {category.name}</h2>
        <form onSubmit={handleSave} className="modal-form">
          <div className="form-field">
            <label>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-field">
            <label>Flag Message</label>
            <input type="text" value={flagMessage} onChange={(e) => setFlagMessage(e.target.value)} required />
          </div>
          <div className="form-field">
            <label>Icon URL</label>
            <input type="url" placeholder="Paste URL from Firebase Storage" value={iconUrl} onChange={(e) => setIconUrl(e.target.value)} required />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// --- Main Category Management Component ---
function ManageCategoriesPage() {
  // All hooks MUST be called at the top level, unconditionally.
  const auth = useAuth();
  const [newName, setNewName] = useState('');
  const [newFlagMessage, setNewFlagMessage] = useState('');
  const [newIconUrl, setNewIconUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!auth || auth.loading) {
      return; 
    }
    if (!auth.currentUser) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(collection(db, 'categories'), (snapshot) => {
      const allCategories = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(allCategories);
      setIsLoading(false);
    }, (err) => {
      setError("Could not load categories.");
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  if (!auth) {
    const style = {padding: '50px', textAlign: 'center', color: 'red', fontWeight: 'bold'};
    return <div style={style}>Authentication Error: AuthProvider is missing.</div>;
  }

  const { currentUser, loading: authLoading } = auth;

  if (authLoading) {
    return <div className="loading-container" style={{padding: '50px', textAlign: 'center'}}>Loading User...</div>;
  }

  if (!currentUser) {
    return <div className="access-denied" style={{padding: '50px', textAlign: 'center', color: 'red'}}>Access Denied. Please log in.</div>;
  }
  
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newName || !newFlagMessage || !newIconUrl) {
      setError("Name, Flag Message, and Icon URL are all required.");
      return;
    }
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const createCategory = httpsCallable(functions, 'createCategory');
      await createCategory({ 
        name: newName, 
        flagMessage: newFlagMessage,
        iconUrl: newIconUrl, 
      });
      
      setSuccessMessage(`Successfully created category: "${newName}"`);
      setNewName('');
      setNewFlagMessage('');
      setNewIconUrl('');
      
    } catch (err) {
      setError(`Failed to create category: ${err.message}`); 
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (category) => {
    setEditingCategory(category);
    setIsEditModalOpen(true);
  };

  const handleSaveChanges = async (updateData) => {
    setIsSaving(true);
    setError('');

    try {
      const updateCategory = httpsCallable(functions, 'updateCategory');
      await updateCategory({
          id: updateData.id,
          name: updateData.name,
          flagMessage: updateData.flagMessage,
          iconUrl: updateData.iconUrl
      });
      setIsEditModalOpen(false);
      setEditingCategory(null);
    } catch (err) {
        alert(`Failed to update category: ${err.message}`);
    } finally {
        setIsSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryToDelete) => {
    const confirmation = `Are you sure you want to delete "${categoryToDelete.name}"? This cannot be undone.`;
    if (window.confirm(confirmation)) {
      try {
        const deleteCategory = httpsCallable(functions, 'deleteCategory');
        await deleteCategory({ id: categoryToDelete.id });
      } catch (err) {
        setError(`Failed to delete category: ${err.message}`);
      }
    }
  };

  return (
    <>
      <EditCategoryModal
        category={editingCategory}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveChanges}
        isSaving={isSaving}
      />

      <div className="category-management-section">
        <h2 className="list-header">Add a New Category</h2>
        <form onSubmit={handleAddCategory} className="category-form">
          {error && <p className="form-error-message">{error}</p>}
          {successMessage && <p className="form-success-message">{successMessage}</p>}
          
          <div className="form-grid-categories">
            <div className="form-field">
              <label htmlFor="catName">Name</label>
              <input id="catName" type="text" value={newName} onChange={e => setNewName(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="catFlagMessage">Flag Message</label>
              <input id="catFlagMessage" type="text" value={newFlagMessage} onChange={e => setNewFlagMessage(e.target.value)} required />
            </div>
            <div className="form-field full-width">
                <label htmlFor="catIconUrl">Icon URL</label>
                <input id="catIconUrl" type="url" placeholder="Paste URL from Firebase Storage" value={newIconUrl} onChange={e => setNewIconUrl(e.target.value)} required />
            </div>
          </div>

          <div className="form-action-bar">
            <button type="submit" className="btn-submit-category" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>

      <div className="category-management-section">
        <h2 className="list-header">Current Categories</h2>
        {isLoading ? <p>Loading categories...</p> : (
          <ul className="category-list">
            {categories.map(cat => (
              <li key={cat.id} className="category-list-item">
                <div className="category-list-icon">
                  {cat.iconUrl ? (
                    <img 
                      src={cat.iconUrl} 
                      alt={cat.name} 
                      style={{width: '40px', height: '40px', objectFit: 'contain'}} 
                    />
                  ) : (
                    <div style={{
                      width: '40px', 
                      height: '40px', 
                      backgroundColor: '#e2e8f0', 
                      borderRadius: '6px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '10px', 
                      color: '#718096'
                    }}>
                      No Icon
                    </div>
                  )}
                </div>
                <div className="category-info">
                  <strong>{cat.name}</strong>
                  <span>{cat.flagMessage}</span>
                </div>
                <div className="category-actions">
                  <button className="btn btn-edit" onClick={() => handleEditClick(cat)}>Edit</button>
                  <button className="btn btn-delete" onClick={() => handleDeleteCategory(cat)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export default ManageCategoriesPage;

